import { useMemo, useState } from "react";
import {
  AreaChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Cell,
} from "recharts";
import type { MonteCarloResult } from "../utils/monteCarlo";

interface MonteCarloAnalysisProps {
  result: MonteCarloResult;
  onClose: () => void;
}

function formatCurrency(value: number): string {
  if (Math.abs(value) >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1)}M`;
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr + "T12:00:00");
  return date.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
}

function StatCard({
  label,
  value,
  subValue,
  color,
}: {
  label: string;
  value: string;
  subValue?: string;
  color: "green" | "red" | "amber" | "blue" | "purple";
}) {
  const colorMap = {
    green: "text-emerald-400",
    red: "text-red-400",
    amber: "text-amber-400",
    blue: "text-primary-400",
    purple: "text-violet-400",
  };
  return (
    <div className="p-3 rounded-lg bg-surface-900/50 border border-surface-700/30">
      <div className="text-xs text-surface-500 font-medium uppercase tracking-wider mb-1">
        {label}
      </div>
      <div className={`text-lg font-bold ${colorMap[color]}`}>{value}</div>
      {subValue && (
        <div className="text-xs text-surface-500 mt-0.5">{subValue}</div>
      )}
    </div>
  );
}

export function MonteCarloAnalysis({
  result,
  onClose,
}: MonteCarloAnalysisProps) {
  const { stats, bands } = result;
  const [activeTab, setActiveTab] = useState<"fan" | "distribution">("fan");

  // Build distribution histogram for CAGR
  const distributionData = useMemo(() => {
    // We'll derive from the band data's final percentiles
    // But for a proper histogram, compute from stats
    const bins = 30;
    const min = stats.percentile5CAGR - 5;
    const max = stats.percentile95CAGR + 5;
    const binWidth = (max - min) / bins;
    const histogram: { range: string; mid: number; count: number; isOriginal: boolean }[] = [];

    for (let i = 0; i < bins; i++) {
      const lo = min + i * binWidth;
      const hi = lo + binWidth;
      const mid = (lo + hi) / 2;
      // Approximate normal distribution using stats
      const z = (mid - stats.medianCAGR) / ((stats.percentile95CAGR - stats.percentile5CAGR) / 3.29);
      const density = Math.exp(-0.5 * z * z);
      const isOriginal =
        stats.originalCAGR >= lo && stats.originalCAGR < hi;
      histogram.push({
        range: `${lo.toFixed(0)}%`,
        mid,
        count: Math.round(density * 100),
        isOriginal,
      });
    }
    return histogram;
  }, [stats]);

  // Drawdown distribution
  const ddDistributionData = useMemo(() => {
    const bins = 20;
    const min = stats.percentile5MaxDrawdown - 5;
    const max = Math.min(stats.percentile95MaxDrawdown + 5, 0);
    const binWidth = (max - min) / bins;
    const histogram: { range: string; mid: number; count: number; isOriginal: boolean }[] = [];

    for (let i = 0; i < bins; i++) {
      const lo = min + i * binWidth;
      const hi = lo + binWidth;
      const mid = (lo + hi) / 2;
      const z = (mid - stats.medianMaxDrawdown) / ((stats.percentile95MaxDrawdown - stats.percentile5MaxDrawdown) / 3.29);
      const density = Math.exp(-0.5 * z * z);
      const isOriginal =
        stats.originalMaxDrawdown >= lo && stats.originalMaxDrawdown < hi;
      histogram.push({
        range: `${lo.toFixed(0)}%`,
        mid,
        count: Math.round(density * 100),
        isOriginal,
      });
    }
    return histogram;
  }, [stats]);

  return (
    <div className="glass-card p-6 animate-slide-up">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-violet-500/20 flex items-center justify-center">
            <svg
              className="w-5 h-5 text-violet-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-surface-100">
              Monte Carlo Simulation
            </h3>
            <span className="text-xs text-surface-500">
              5,000 simulations · 20-day block bootstrap ·{" "}
              {result.computeTimeMs.toFixed(0)}ms
            </span>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-surface-400 hover:text-surface-200 p-1.5 rounded hover:bg-surface-700 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Summary Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <StatCard
          label="Median CAGR"
          value={`${stats.medianCAGR.toFixed(1)}%`}
          subValue={`Original: ${stats.originalCAGR.toFixed(1)}%`}
          color="green"
        />
        <StatCard
          label="CAGR Range (90%)"
          value={`${stats.percentile5CAGR.toFixed(1)}% — ${stats.percentile95CAGR.toFixed(1)}%`}
          subValue="5th to 95th percentile"
          color="blue"
        />
        <StatCard
          label="Median Max DD"
          value={`${stats.medianMaxDrawdown.toFixed(1)}%`}
          subValue={`Original: ${stats.originalMaxDrawdown.toFixed(1)}%`}
          color="amber"
        />
        <StatCard
          label="Worst-Case DD (5%)"
          value={`${stats.percentile5MaxDrawdown.toFixed(1)}%`}
          subValue="5th percentile"
          color="red"
        />
        <StatCard
          label="Profit Probability"
          value={`${(stats.profitProbability * 100).toFixed(1)}%`}
          subValue={`Over ${stats.years.toFixed(1)} years`}
          color={stats.profitProbability >= 0.8 ? "green" : "amber"}
        />
        <StatCard
          label="Ruin Risk (>50% loss)"
          value={`${(stats.ruinProbability * 100).toFixed(1)}%`}
          subValue="Probability"
          color={stats.ruinProbability < 0.05 ? "green" : "red"}
        />
      </div>

      {/* Tab Switcher */}
      <div className="flex items-center gap-1 bg-surface-800 rounded-lg p-0.5 w-fit mb-5">
        <button
          onClick={() => setActiveTab("fan")}
          className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
            activeTab === "fan"
              ? "bg-surface-600 text-surface-100"
              : "text-surface-400 hover:text-surface-200"
          }`}
        >
          Confidence Fan
        </button>
        <button
          onClick={() => setActiveTab("distribution")}
          className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
            activeTab === "distribution"
              ? "bg-surface-600 text-surface-100"
              : "text-surface-400 hover:text-surface-200"
          }`}
        >
          Distributions
        </button>
      </div>

      {/* Fan Chart */}
      {activeTab === "fan" && (
        <div>
          {/* Legend */}
          <div className="flex flex-wrap items-center gap-4 mb-3 text-xs">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-1.5 rounded-full bg-violet-400/40" />
              <span className="text-surface-400">5th–95th percentile</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-1.5 rounded-full bg-violet-400/70" />
              <span className="text-surface-400">25th–75th percentile</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-1.5 rounded-full bg-violet-400" />
              <span className="text-surface-400">Median</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-1.5 rounded-full bg-emerald-400" />
              <span className="text-surface-400">Original</span>
            </div>
          </div>

          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={bands} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                <defs>
                  <linearGradient id="mc-outer" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.15} />
                    <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0.03} />
                  </linearGradient>
                  <linearGradient id="mc-inner" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(148, 163, 184, 0.08)"
                  vertical={false}
                />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  tick={{ fontSize: 11, fill: "#64748b" }}
                  axisLine={{ stroke: "rgba(148, 163, 184, 0.15)" }}
                  tickLine={false}
                  interval="preserveStartEnd"
                  minTickGap={60}
                />
                <YAxis
                  tickFormatter={(v: number) => formatCurrency(v)}
                  tick={{ fontSize: 11, fill: "#64748b" }}
                  axisLine={false}
                  tickLine={false}
                  width={70}
                />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0]?.payload;
                    if (!d) return null;
                    return (
                      <div className="bg-surface-800 border border-surface-600 rounded-lg p-3 shadow-lg text-sm">
                        <p className="text-surface-300 mb-2">{label}</p>
                        <div className="space-y-1">
                          <div className="flex justify-between gap-4">
                            <span className="text-emerald-400">Original:</span>
                            <span className="text-surface-200 font-medium">{formatCurrency(d.original)}</span>
                          </div>
                          <div className="flex justify-between gap-4">
                            <span className="text-violet-400">Median:</span>
                            <span className="text-surface-200 font-medium">{formatCurrency(d.median)}</span>
                          </div>
                          <div className="flex justify-between gap-4">
                            <span className="text-surface-400">90% Range:</span>
                            <span className="text-surface-300">
                              {formatCurrency(d.p5)} — {formatCurrency(d.p95)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  }}
                />

                {/* 5th-95th band (outer) */}
                <Area
                  type="monotone"
                  dataKey="p95"
                  stroke="none"
                  fill="url(#mc-outer)"
                  fillOpacity={1}
                  isAnimationActive={false}
                />
                <Area
                  type="monotone"
                  dataKey="p5"
                  stroke="none"
                  fill="var(--color-surface-950, #0a0e1a)"
                  fillOpacity={1}
                  isAnimationActive={false}
                />

                {/* 25th-75th band (inner) */}
                <Area
                  type="monotone"
                  dataKey="p75"
                  stroke="none"
                  fill="url(#mc-inner)"
                  fillOpacity={1}
                  isAnimationActive={false}
                />
                <Area
                  type="monotone"
                  dataKey="p25"
                  stroke="none"
                  fill="var(--color-surface-950, #0a0e1a)"
                  fillOpacity={1}
                  isAnimationActive={false}
                />

                {/* Median line */}
                <Line
                  type="monotone"
                  dataKey="median"
                  stroke="#8b5cf6"
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />

                {/* Original equity line */}
                <Line
                  type="monotone"
                  dataKey="original"
                  stroke="#34d399"
                  strokeWidth={2}
                  dot={false}
                  strokeDasharray="6 3"
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Distribution Charts */}
      {activeTab === "distribution" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* CAGR Distribution */}
          <div>
            <h4 className="text-sm font-semibold text-surface-300 mb-3">
              CAGR Distribution
            </h4>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={distributionData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(148, 163, 184, 0.08)"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="range"
                    tick={{ fontSize: 10, fill: "#64748b" }}
                    axisLine={{ stroke: "rgba(148, 163, 184, 0.15)" }}
                    tickLine={false}
                    interval={4}
                  />
                  <YAxis hide />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0]?.payload;
                      if (!d) return null;
                      return (
                        <div className="bg-surface-800 border border-surface-600 rounded-lg p-2.5 shadow-lg text-sm">
                          <span className="text-surface-300">
                            CAGR ~{d.mid.toFixed(1)}%
                          </span>
                          {d.isOriginal && (
                            <span className="ml-2 text-emerald-400 text-xs">
                              ◄ Your strategy
                            </span>
                          )}
                        </div>
                      );
                    }}
                  />
                  <Bar dataKey="count" radius={[2, 2, 0, 0]} isAnimationActive={false}>
                    {distributionData.map((entry, index) => (
                      <Cell
                        key={index}
                        fill={entry.isOriginal ? "#34d399" : "#8b5cf6"}
                        fillOpacity={entry.isOriginal ? 0.9 : 0.5}
                      />
                    ))}
                  </Bar>
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center justify-center gap-4 mt-2 text-xs text-surface-500">
              <span>
                5th: <span className="text-surface-300">{stats.percentile5CAGR.toFixed(1)}%</span>
              </span>
              <span>
                Median: <span className="text-violet-400 font-medium">{stats.medianCAGR.toFixed(1)}%</span>
              </span>
              <span>
                95th: <span className="text-surface-300">{stats.percentile95CAGR.toFixed(1)}%</span>
              </span>
            </div>
          </div>

          {/* Max Drawdown Distribution */}
          <div>
            <h4 className="text-sm font-semibold text-surface-300 mb-3">
              Max Drawdown Distribution
            </h4>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={ddDistributionData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(148, 163, 184, 0.08)"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="range"
                    tick={{ fontSize: 10, fill: "#64748b" }}
                    axisLine={{ stroke: "rgba(148, 163, 184, 0.15)" }}
                    tickLine={false}
                    interval={3}
                  />
                  <YAxis hide />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0]?.payload;
                      if (!d) return null;
                      return (
                        <div className="bg-surface-800 border border-surface-600 rounded-lg p-2.5 shadow-lg text-sm">
                          <span className="text-surface-300">
                            Max DD ~{d.mid.toFixed(1)}%
                          </span>
                          {d.isOriginal && (
                            <span className="ml-2 text-amber-400 text-xs">
                              ◄ Your strategy
                            </span>
                          )}
                        </div>
                      );
                    }}
                  />
                  <Bar dataKey="count" radius={[2, 2, 0, 0]} isAnimationActive={false}>
                    {ddDistributionData.map((entry, index) => (
                      <Cell
                        key={index}
                        fill={entry.isOriginal ? "#fbbf24" : "#ef4444"}
                        fillOpacity={entry.isOriginal ? 0.9 : 0.4}
                      />
                    ))}
                  </Bar>
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center justify-center gap-4 mt-2 text-xs text-surface-500">
              <span>
                Worst (5th): <span className="text-red-400">{stats.percentile5MaxDrawdown.toFixed(1)}%</span>
              </span>
              <span>
                Median: <span className="text-amber-400 font-medium">{stats.medianMaxDrawdown.toFixed(1)}%</span>
              </span>
              <span>
                Best (95th): <span className="text-surface-300">{stats.percentile95MaxDrawdown.toFixed(1)}%</span>
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Interpretation Footer */}
      <div className="mt-5 pt-4 border-t border-surface-700/50">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
          <div className="flex items-start gap-2">
            {stats.originalCAGR >= stats.percentile5CAGR &&
            stats.originalCAGR <= stats.percentile95CAGR ? (
              <span className="text-emerald-400 mt-0.5">●</span>
            ) : (
              <span className="text-amber-400 mt-0.5">●</span>
            )}
            <span className="text-surface-400">
              {stats.originalCAGR >= stats.medianCAGR
                ? "Your returns are above the median — trade ordering was favorable"
                : "Your returns fall below the median — trade ordering was unfavorable"}
            </span>
          </div>
          <div className="flex items-start gap-2">
            {Math.abs(stats.percentile95CAGR - stats.percentile5CAGR) < 30 ? (
              <span className="text-emerald-400 mt-0.5">●</span>
            ) : (
              <span className="text-amber-400 mt-0.5">●</span>
            )}
            <span className="text-surface-400">
              {Math.abs(stats.percentile95CAGR - stats.percentile5CAGR) < 30
                ? "Tight distribution — strategy is robust to path dependency"
                : "Wide distribution — returns are highly path-dependent"}
            </span>
          </div>
          <div className="flex items-start gap-2">
            {stats.ruinProbability < 0.02 ? (
              <span className="text-emerald-400 mt-0.5">●</span>
            ) : stats.ruinProbability < 0.1 ? (
              <span className="text-amber-400 mt-0.5">●</span>
            ) : (
              <span className="text-red-400 mt-0.5">●</span>
            )}
            <span className="text-surface-400">
              {stats.ruinProbability < 0.02
                ? "Very low ruin risk — strategy survives most market orderings"
                : stats.ruinProbability < 0.1
                  ? "Moderate ruin risk — consider risk management"
                  : "High ruin risk — strategy is fragile to adverse sequencing"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
